<template>
  <v-container fluid class="blacklist-detail pa-6">

    <!-- Back Button + Title -->
    <v-row class="mb-4" align="center">
      <v-col cols="auto">
        <v-btn text color="primary" @click="$router.back()">
          <v-icon left>mdi-arrow-left</v-icon>
          Back
        </v-btn>
      </v-col>
      <v-col>
        <h2 class="headline font-weight-bold">Blacklist Detail</h2>
      </v-col>
    </v-row>

    <!-- Loading -->
    <v-row v-if="loading" justify="center" class="mt-10">
      <v-progress-circular indeterminate color="primary" size="60" />
    </v-row>

    <template v-else-if="blacklist && blacklist.id">

      <!-- Status Banner -->
      <v-alert
          :color="blacklist.deactivated_by ? 'grey darken-1' : 'red darken-2'"
          dark dense class="mb-6" icon="mdi-alert-circle"
      >
        <strong>Status:</strong>
        {{ blacklist.deactivated_by ? 'Inactive / Deactivated' : 'Active Blacklist' }}
      </v-alert>

      <!-- Main Info Card -->
      <v-card elevation="3" class="mb-6 rounded-lg">
        <v-card-title class="white--text primary py-4 px-6">
          <v-icon left color="white">mdi-account-cancel</v-icon>
          Personal Information
        </v-card-title>
        <v-card-text class="pa-6">
          <v-row>
            <!-- Left Column -->
            <v-col cols="12" md="6">
              <div class="detail-field">
                <div class="field-label"><v-icon x-small>mdi-pound</v-icon> ID</div>
                <div class="field-value">{{ blacklist.id }}</div>
              </div>
              <div class="detail-field">
                <div class="field-label"><v-icon x-small>mdi-account</v-icon> Full Name</div>
                <div class="field-value">{{ blacklist.name || '—' }}</div>
              </div>
              <div class="detail-field">
                <div class="field-label"><v-icon x-small>mdi-flag</v-icon> Country</div>
                <div class="field-value">{{ blacklist.country_name || '—' }}</div>
              </div>
              <div class="detail-field">
                <div class="field-label"><v-icon x-small>mdi-earth</v-icon> Country ID</div>
                <div class="field-value">{{ blacklist.country_id || '—' }}</div>
              </div>
              <div class="detail-field">
                <div class="field-label"><v-icon x-small>mdi-card-account-details</v-icon> Document Number</div>
                <div class="field-value">{{ blacklist.document_number || '—' }}</div>
              </div>
              <div class="detail-field">
                <div class="field-label"><v-icon x-small>mdi-tag</v-icon> Blacklist Category</div>
                <div class="field-value">{{ blacklist.blacklist_category || '—' }}</div>
              </div>
            </v-col>

            <!-- Right Column -->
            <v-col cols="12" md="6">
              <div class="detail-field">
                <div class="field-label"><v-icon x-small>mdi-calendar</v-icon> Expiry / To Date</div>
                <div class="field-value">{{ blacklist.expiry_date || '—' }}</div>
              </div>
              <div class="detail-field">
                <div class="field-label"><v-icon x-small>mdi-gavel</v-icon> Action To Be Taken</div>
                <div class="field-value">{{ blacklist.action_to_be_taken || '—' }}</div>
              </div>
              <div class="detail-field">
                <div class="field-label"><v-icon x-small>mdi-map-marker</v-icon> Hints / Address</div>
                <div class="field-value">{{ blacklist.hints || '—' }}</div>
              </div>
              <div class="detail-field">
                <div class="field-label"><v-icon x-small>mdi-account-edit</v-icon> Added By</div>
                <div class="field-value">{{ blacklist.creator || '—' }}</div>
              </div>
              <div class="detail-field">
                <div class="field-label"><v-icon x-small>mdi-clock-outline</v-icon> Created At</div>
                <div class="field-value">{{ blacklist.created_at || '—' }}</div>
              </div>
            </v-col>
          </v-row>

          <!-- Remarks -->
          <v-divider class="my-4" />
          <div class="field-label mb-2"><v-icon x-small>mdi-text</v-icon> Remarks</div>
          <v-sheet color="grey lighten-4" rounded class="pa-3 body-2">
            {{ blacklist.remarks || 'No remarks provided.' }}
          </v-sheet>

          <!-- Document File -->
          <v-divider class="my-4" />
          <div class="field-label mb-3">
            <v-icon x-small>mdi-file-document</v-icon> Document File
          </div>
          <template v-if="blacklist.document_file">
            <!-- Image -->
            <template v-if="isImage(blacklist.document_file)">
              <v-img
                  :src="blacklist.document_file"
                  max-width="500"
                  max-height="400"
                  contain
                  class="rounded elevation-2 mb-2"
              />
              <a :href="blacklist.document_file" target="_blank">
                <v-btn x-small text color="primary">
                  <v-icon x-small left>mdi-open-in-new</v-icon> Open Full Image
                </v-btn>
              </a>
            </template>
            <!-- PDF -->
            <template v-else-if="isPdf(blacklist.document_file)">
              <iframe
                  :src="blacklist.document_file"
                  width="100%"
                  height="500px"
                  style="border:1px solid #ccc; border-radius:6px;"
              />
              <a :href="blacklist.document_file" target="_blank">
                <v-btn x-small text color="primary" class="mt-1">
                  <v-icon x-small left>mdi-open-in-new</v-icon> Open PDF
                </v-btn>
              </a>
            </template>
            <!-- Other -->
            <template v-else>
              <a :href="blacklist.document_file" target="_blank">
                <v-btn outlined color="primary" small>
                  <v-icon left small>mdi-download</v-icon> Download Document
                </v-btn>
              </a>
            </template>
          </template>
          <template v-else>
            <div class="field-value">—</div>
          </template>

        </v-card-text>
      </v-card>

      <!-- Deactivation Info Card -->
      <v-card elevation="3" class="mb-6 rounded-lg">
        <v-card-title class="white--text grey darken-2 py-4 px-6">
          <v-icon left color="white">mdi-account-remove</v-icon>
          Deactivation Information
        </v-card-title>
        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12" md="6">
              <div class="detail-field">
                <div class="field-label"><v-icon x-small>mdi-account-off</v-icon> Deactivated By</div>
                <div class="field-value">{{ blacklist.deactivated_by || '—' }}</div>
              </div>
              <div class="detail-field">
                <div class="field-label"><v-icon x-small>mdi-text</v-icon> Deactivated Remarks</div>
                <div class="field-value">{{ blacklist.deactivated_remarks || '—' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="field-label mb-3">
                <v-icon x-small>mdi-file</v-icon> Deactivated File
              </div>
              <template v-if="blacklist.deactivated_file">
                <!-- Image -->
                <template v-if="isImage(blacklist.deactivated_file)">
                  <v-img
                      :src="blacklist.deactivated_file"
                      max-width="400"
                      max-height="300"
                      contain
                      class="rounded elevation-2 mb-2"
                  />
                  <a :href="blacklist.deactivated_file" target="_blank">
                    <v-btn x-small text color="grey darken-2">
                      <v-icon x-small left>mdi-open-in-new</v-icon> Open Full Image
                    </v-btn>
                  </a>
                </template>
                <!-- PDF -->
                <template v-else-if="isPdf(blacklist.deactivated_file)">
                  <iframe
                      :src="blacklist.deactivated_file"
                      width="100%"
                      height="400px"
                      style="border:1px solid #ccc; border-radius:6px;"
                  />
                  <a :href="blacklist.deactivated_file" target="_blank">
                    <v-btn x-small text color="grey darken-2" class="mt-1">
                      <v-icon x-small left>mdi-open-in-new</v-icon> Open PDF
                    </v-btn>
                  </a>
                </template>
                <!-- Other -->
                <template v-else>
                  <a :href="blacklist.deactivated_file" target="_blank">
                    <v-btn outlined color="grey darken-2" small>
                      <v-icon left small>mdi-download</v-icon> Download File
                    </v-btn>
                  </a>
                </template>
              </template>
              <template v-else>
                <div class="field-value">—</div>
              </template>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

    </template>

    <!-- Not found -->
    <v-row v-else justify="center" class="mt-10">
      <v-col cols="auto" class="text-center">
        <v-icon size="64" color="grey lighten-1">mdi-account-cancel</v-icon>
        <div class="headline grey--text mt-4">Record not found.</div>
      </v-col>
    </v-row>

  </v-container>
</template>

<script>
import Http from "axios";
import { API_URL, VERSION } from "../../config/config.js";

export default {
  name: 'BlacklistDetail',

  data() {
    return {
      blacklist: {},
      loading: false,
    };
  },

  methods: {
    getBlacklistDetail() {
      this.loading = true;
      const id = this.$route.params.id;
      Http.get(`${API_URL}${VERSION}/blacklists/${id}`)
          .then(response => {
            this.blacklist = response.data.data;
          })
          .catch(() => {
            this.$store.commit("Notification/showError", "Failed to load blacklist detail.");
          })
          .finally(() => {
            this.loading = false;
          });
    },

    isImage(url) {
      if (!url) return false;
      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(url);
    },

    isPdf(url) {
      if (!url) return false;
      return /\.pdf(\?.*)?$/i.test(url);
    },
  },

  mounted() {
    this.getBlacklistDetail();
  }
};
</script>

<style scoped>
.blacklist-detail {
  max-width: 1100px;
  margin: 0 auto;
}
.detail-field {
  margin-bottom: 18px;
}
.field-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #888;
  margin-bottom: 2px;
}
.field-label .v-icon {
  margin-right: 4px;
  color: #888;
}
.field-value {
  font-size: 15px;
  font-weight: 500;
  color: #222;
}
</style>